import { useRef, type Ref } from 'react';
import type { StatusTone } from '../../../design-system';
import type { TenantSettingsPort } from '../port/TenantSettingsPort';
import { errorCode, errorDetails } from './resource';
import type {
  ReferenceDataBlocker,
  ReferenceDataItem,
  ReferenceDataStatus,
  ReferenceJobType,
  ReferenceLocationType,
} from './types';
export type ReferenceDataResource<T> = {
  data?: T[];
  loading: boolean;
  error?: string;
  reload: () => void;
};
export type ReferenceSettingsCommonProps = {
  port: TenantSettingsPort;
  fixtureMode: boolean;
  canRead: boolean;
  canWrite: boolean;
  onBack: () => void;
  titleRef?: Ref<HTMLHeadingElement>;
};
export const statusOptions = [
  {
    label: '전체',
    value: '',
  },
  {
    label: '사용 예정',
    value: 'SCHEDULED',
  },
  {
    label: '사용 중',
    value: 'ACTIVE',
  },
  {
    label: '사용 종료 예정',
    value: 'END_SCHEDULED',
  },
  {
    label: '사용 종료',
    value: 'ENDED',
  },
];
export const locationTypeOptions: Array<{
  value: ReferenceLocationType;
  label: string;
}> = [
  {
    value: 'OFFICE',
    label: '사무실',
  },
  {
    value: 'REMOTE',
    label: '원격 근무지',
  },
  {
    value: 'CLIENT_SITE',
    label: '고객사',
  },
  {
    value: 'OTHER',
    label: '기타',
  },
];
export const workerTypeOptions: Array<{
  value: ReferenceJobType['workerType'];
  label: string;
}> = [
  {
    value: 'EMPLOYEE',
    label: '임직원',
  },
  {
    value: 'CONTINGENT_WORKER',
    label: '외부 인력',
  },
];
export const blockerLabels: Record<string, string> = {
  WORKER_JOB: '현재·예약된 구성원 배치',
  SUPERVISORY_ORGANIZATION: '조직 기본 근무지',
  POSITION: '포지션 제약',
  JOB_PROFILE: '연결된 직무',
  OPEN_CHANGE: '처리 중인 인사 변경',
};
export function todayInSeoul() {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Seoul',
  }).format(new Date());
}
export function statusLabel(status: ReferenceDataStatus) {
  if (status === 'SCHEDULED') return '사용 예정';
  if (status === 'ACTIVE') return '사용 중';
  if (status === 'END_SCHEDULED') return '사용 종료 예정';
  return '사용 종료';
}
export function statusTone(status: ReferenceDataStatus): StatusTone {
  if (status === 'SCHEDULED') return 'info';
  if (status === 'ACTIVE') return 'success';
  if (status === 'END_SCHEDULED') return 'warning';
  return 'neutral';
}
export function locationTypeLabel(type: ReferenceLocationType) {
  return locationTypeOptions.find((option) => option.value === type)?.label ?? type;
}
export function workerTypeLabel(type: ReferenceJobType['workerType']) {
  return workerTypeOptions.find((option) => option.value === type)?.label ?? type;
}
export function fixtureReferenceCode() {
  const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  const initialAlphabet = 'ABCDEFGHJKMNPQRSTVWXYZ';
  const random = crypto.getRandomValues(new Uint8Array(8));
  const payload =
    initialAlphabet[random[0] % initialAlphabet.length] +
    Array.from(random.slice(1), (value) => alphabet[value & 31]).join('');
  return `${payload.slice(0, 4)}-${payload.slice(4)}`;
}
export function validDescriptor(value: string) {
  const length = Array.from(value.trim()).length;
  return length >= 1 && length <= 120;
}
export function validDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value;
}
export function validReason(value: string) {
  const length = Array.from(value.trim()).length;
  return length >= 1 && length <= 500;
}
export function sortCatalog<T extends ReferenceDataItem>(items: T[]) {
  return [...items].sort((left, right) => left.descriptor.localeCompare(right.descriptor, 'ko'));
}
export function replaceCatalogItem<T extends ReferenceDataItem>(items: T[], item: T) {
  const exists = items.some((candidate) => candidate.id === item.id);
  return sortCatalog(
    exists ? items.map((candidate) => (candidate.id === item.id ? item : candidate)) : [...items, item],
  );
}
export function replaceCatalogItems<T extends ReferenceDataItem>(items: T[], replacements: readonly T[]) {
  const next = new Map(items.map((item) => [item.id, item]));
  replacements.forEach((item) => next.set(item.id, item));
  return sortCatalog([...next.values()]);
}
export function filterCatalog<T extends ReferenceDataItem>(items: T[], query: string, status: string) {
  const normalizedQuery = query.trim().toLocaleLowerCase('ko');
  return items.filter((item) => {
    if (status && item.status !== status) return false;
    if (!normalizedQuery) return true;
    return (
      item.descriptor.toLocaleLowerCase('ko').includes(normalizedQuery) ||
      item.code.toLocaleLowerCase('ko').includes(normalizedQuery)
    );
  });
}
export function useOperationKey() {
  const operation = useRef<
    | {
        signature: string;
        key: string;
      }
    | undefined
  >(undefined);
  return {
    forPayload(payload: unknown) {
      const signature = JSON.stringify(payload);
      if (!operation.current || operation.current.signature !== signature)
        operation.current = {
          signature,
          key: crypto.randomUUID(),
        };
      return operation.current.key;
    },
    reset() {
      operation.current = undefined;
    },
  };
}
export function apiErrorMessage(cause: unknown, fallback: string) {
  if (errorCode(cause) === 'REFERENCE_TIMELINE_CONFLICT' || errorCode(cause) === 'VERSION_CONFLICT')
    return '다른 사용자가 이 기준정보를 먼저 변경했습니다. 최신 목록을 다시 불러온 뒤 확인해 주세요.';
  if (errorCode(cause) === 'REFERENCE_CODE_CONFLICT')
    return '참조 코드를 자동 발급하지 못했습니다. 다시 시도해 주세요.';
  return cause instanceof Error ? cause.message : fallback;
}
export function referenceBlockers(cause: unknown): ReferenceDataBlocker[] {
  const details = errorDetails(cause);
  if (errorCode(cause) !== 'REFERENCE_IN_USE' || !details || typeof details !== 'object') return [];
  const blockers = (
    details as {
      blockers?: unknown;
    }
  ).blockers;
  if (!Array.isArray(blockers)) return [];
  return blockers.filter((blocker): blocker is ReferenceDataBlocker => {
    if (!blocker || typeof blocker !== 'object') return false;
    const candidate = blocker as {
      type?: unknown;
      count?: unknown;
    };
    return typeof candidate.type === 'string' && typeof candidate.count === 'number' && candidate.count >= 0;
  });
}
